import { computed, ref } from 'vue';
import { useApi } from './useApi.js';

const SESSION_PROMPT_KEY = 'merlot.adminSetupWizard.prompted';

export function resetAdminSetupWizardSession() {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(SESSION_PROMPT_KEY);
  }
}

const steps = ref([]);
const completedCount = ref(0);
const totalCount = ref(0);
const allComplete = ref(false);
const wizard = ref({ dismissed: false, promptCount: 0, maxLoginPrompts: 3 });
const showWizardModal = ref(false);
const loading = ref(false);
let promptInFlight = false;

function firstIncompleteStep() {
  return (steps.value || []).find((s) => !s.complete) || null;
}

export function useAdminSetupChecklist() {
  const { get, patch } = useApi();

  async function refresh() {
    loading.value = true;
    try {
      const res = await get('/admin/setup-checklist');
      const data = res?.data || {};
      steps.value = Array.isArray(data.steps) ? data.steps : [];
      completedCount.value = data.completedCount || 0;
      totalCount.value = data.totalCount || 0;
      allComplete.value = !!data.allComplete;
      wizard.value = data.wizard || { dismissed: false, promptCount: 0, maxLoginPrompts: 3 };
      if (allComplete.value) {
        showWizardModal.value = false;
      }
      return data;
    } catch {
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function maybePromptOnLogin() {
    if (promptInFlight) return;
    promptInFlight = true;
    try {
      const data = await refresh();
      if (!data) return;
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SESSION_PROMPT_KEY)) {
        return;
      }
      if (data.allComplete || data.wizard?.dismissed) return;
      const max = data.wizard?.maxLoginPrompts ?? 3;
      if ((data.wizard?.promptCount || 0) >= max) return;
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(SESSION_PROMPT_KEY, '1');
      }
      try {
        await patch('/admin/setup-checklist/wizard', { incrementPrompt: true });
      } catch {
        /* still show the coach once this session */
      }
      showWizardModal.value = true;
      await refresh();
    } finally {
      promptInFlight = false;
    }
  }

  async function dismissPermanently() {
    try {
      await patch('/admin/setup-checklist/wizard', { dismissed: true });
    } finally {
      showWizardModal.value = false;
    }
    await refresh();
  }

  function skipForNow() {
    showWizardModal.value = false;
  }

  const showSidebar = computed(() => !allComplete.value && (steps.value || []).length > 0);

  return {
    steps,
    completedCount,
    totalCount,
    allComplete,
    wizard,
    showWizardModal,
    loading,
    showSidebar,
    firstIncompleteStep,
    refresh,
    maybePromptOnLogin,
    dismissPermanently,
    skipForNow
  };
}
